import dynamic from 'next/dynamic';

import type { PropsWithChildren } from 'react';

import { useUserAdSettings } from '@shared/common/components/NavLayout/AdBlocks';
import { ProfileMenuBalance } from '@shared/common/components/NavLayout/ProfileMenuDialog/ProfileMenuBalance';
import { ProfileMenuMembership } from '@shared/common/components/NavLayout/ProfileMenuDialog/ProfileMenuMembership';
import ProfileMenuDialogKyc from '@shared/common/components/NavLayout/ProfileMenuDialogKyc';
import { nextProjectUrl } from '@shared/constants/urls';

import * as Dialog from '@radix-ui/react-dialog';
import { Overlay } from '@ui/uikit/components/common/Overlays';
import { CrossLg } from '@ui/uikit/components/icons/mono/CrossLg';
import { cn } from '@ui/uikit/utils';

import { useNavLayout } from '../NavLayoutContext';
import NavLayoutLink from '../NavLayoutLink';
import type { NavLayoutLinkBase, NavLayoutUser } from '../types';
import UserAvatar from '../UserAvatar';

const GlobalInlineProfileMiddleBanner = dynamic(
  () => import('@shared/common/components/NavLayout/AdBlocks/GlobalInlineProfileMiddleBanner'),
  {
    ssr: false,
  },
);

export type ProfileNavItem = NavLayoutLinkBase & {
  hidden?: (data: { user: NavLayoutUser }) => boolean;
};

export type ProfileNavGroup = {
  id: string;
  className?: string;
  items: ProfileNavItem[];
  hidden?: (data: { user: NavLayoutUser }) => boolean;
};

export const profilePublicNav: ProfileNavGroup = {
  id: 'public',
  items: [
    {
      id: 'karma-and-rewards',
      label: 'Karma & Rewards',
      href: nextProjectUrl.store('/profile/karma'),
    },
    {
      id: 'magic-id',
      label: 'Magic ID',
      href: nextProjectUrl.store('/profile/magic-id'),
    },
    {
      id: 'magic-membership',
      label: 'Membership & Balances',
      href: nextProjectUrl.store('/profile/membership/dashboard'),
    },
    {
      id: 'invite-friends',
      label: 'Invite Friends',
      href: nextProjectUrl.store('/refer-a-friend'),
    },
    {
      id: 'vote-and-earn',
      label: 'Vote & Earn',
      href: nextProjectUrl.store('/profile/validation-tasks'),
    },
  ],
};

export const profileDeveloperNav: ProfileNavGroup = {
  id: 'developer',
  hidden: ({ user }) => !user.data.attributes.creatorId,
  items: [
    {
      id: 'settings',
      label: 'Developer Profile',
      href: nextProjectUrl.store('/profile/edit-developer-profile'),
    },
    {
      id: 'my-applications',
      label: 'My Applications',
      href: nextProjectUrl.store('/profile/developer-dapps'),
    },
    {
      id: 'campaign-review',
      label: 'Campaign Review',
      href: nextProjectUrl.store('/profile/developer-campaign'),
    },
  ],
};

export const becomeDeveloperLink: ProfileNavItem = {
  id: 'become-developer',
  href: nextProjectUrl.store('/profile/edit-developer-profile'),
  label: 'Become Developer',
  hidden: ({ user }) => Boolean(user.data.attributes.creatorId),
};

const profileMenuNav: ProfileNavGroup[] = [
  profilePublicNav,
  profileDeveloperNav,
  {
    id: 'general',
    className: 'mt-auto',
    items: [
      // {
      //   id: 'settings-and-privacy',
      //   label: 'Settings & Privacy',
      //   href: nextProjectUrl.store('/profile/settings'),
      // },
      {
        // NOTE: logic inside of component
        id: 'support',
        label: 'Support',
      },
      {
        id: 'docs',
        label: 'Docs',
        external: true,
        href: 'https://docs.magicsquare.io',
      },
      becomeDeveloperLink,
    ],
  },
];

export interface ProfileMenuDialogProps extends Dialog.DialogProps {
  onLogout?: () => void;
  onSupport?: () => void;
  user: NavLayoutUser;
  portalMode?: 'none' | 'default' | 'overlay';
}

const ProfileMenuDialogPortal = ({
  mode: portalMode,
  children,
}: PropsWithChildren<{ mode: 'none' | 'default' | 'overlay' }>) => {
  switch (portalMode) {
    case 'none':
      return children;

    case 'default':
      return <Dialog.Portal>{children}</Dialog.Portal>;

    case 'overlay':
      return <Overlay>{children}</Overlay>;
  }
};

export const ProfileMenuDialog = ({
  onLogout,
  onSupport,
  user,
  onOpenChange,
  portalMode = 'default',
  ...props
}: ProfileMenuDialogProps) => {
  const { showKyc } = useNavLayout();
  const { showGlobalInlineProfileMiddleBanner } = useUserAdSettings();

  const closeDialog = () => {
    onOpenChange?.(false);
  };

  const handleLogoutButtonClick = () => {
    onLogout?.();
    closeDialog();
  };

  const navItems = user.data.attributes.developerId
    ? profileMenuNav
    : profileMenuNav.filter((group) => group.id !== 'developer');

  const navItemClassName =
    'msq-sub-menu-link msq-sub-menu-link-mobile text-caption-sm-strong-m xl-msq:text-caption-sm-strong-d';

  const navGroupClassName = 'p-2 flex flex-col gap-1 xl-msq:p-4';

  return (
    <Dialog.Root onOpenChange={onOpenChange} {...props}>
      <ProfileMenuDialogPortal mode={portalMode}>
        <Dialog.Overlay className="msq-dialog msq-dialog-mobile-menu-m xl-msq:msq-dialog-mobile-menu-d msq-dialog-animate">
          <Dialog.Content
            forceMount
            aria-describedby={undefined}
            className={cn(
              // to hide divider when they on rounded corners of the panel
              'overflow-hidden',
              'z-20 msq-dialog-content sm-msq:msq-dialog-content-sm sm-msq:msq-dialog-content-right xl-msq:max-w-[400px]',
            )}
          >
            <div className="msq-dialog-header">
              <Dialog.Title className="msq-dialog-title ">Profile</Dialog.Title>

              <Dialog.Close asChild>
                <button className="msq-btn msq-btn-ghost msq-btn-icon-md msq-dialog-icon-button">
                  <CrossLg className="msq-btn msq-btn-icon-child" />
                </button>
              </Dialog.Close>
            </div>

            <div className="msq-dialog-body flex flex-col p-0">
              <div className="p-4 pb-3 xl-msq:px-6 xl-msq:pt-5 xl-msq:pb-1.5">
                <div className="flex items-center gap-2 xl-msq:gap-2.5 min-h-10 pb-4 xl-msq:pb-5 shadow-inset-border-bottom shadow-base-border-light">
                  <UserAvatar
                    className="msq-avatar-md"
                    src={user.data.attributes.icon}
                    membershipTier={user.data.membershipTier.attributes.key}
                  />

                  <div className="flex flex-col gap-0.5 xl-msq:gap-0 justify-center overflow-hidden">
                    <span
                      className="text-caption-sm-strong-a truncate"
                      data-qa="profile-displayed-name"
                    >
                      {user.data.attributes.displayedName || 'Noname'}
                    </span>

                    <span
                      className="text-caption-xs-a text-base-text-tertiary truncate"
                      data-qa="profile-name"
                    >
                      {user.data.attributes.name || 'Noname'}
                    </span>
                  </div>
                </div>

                {showKyc && (
                  <ProfileMenuDialogKyc
                    className="pb-5 xl-msq:pb-[1.625rem] shadow-inset-border-bottom shadow-base-border-light"
                    user={user.data}
                    onOpenChange={onOpenChange}
                  />
                )}
                <div className="py-4 shadow-inset-border-bottom shadow-base-border-light">
                  <ProfileMenuBalance user={user.data} />
                </div>

                <ProfileMenuMembership
                  user={user.data}
                  onClick={closeDialog}
                  isExternalLink={true}
                />

                {showGlobalInlineProfileMiddleBanner && (
                  <GlobalInlineProfileMiddleBanner userId={user?.data.id} />
                )}
              </div>

              {/* shadow is used to make it pixel perfect because border is taking space */}
              <nav className="flex-1 flex flex-col [&>*:not(:first-child)]:shadow-inset-border-top [&>*:not(:first-child)]:shadow-base-border-light">
                {navItems.map((navGroup) => {
                  const isNavGroupHidden = navGroup.hidden && navGroup.hidden({ user });

                  if (isNavGroupHidden) {
                    return null;
                  }

                  return (
                    <div className={cn(navGroupClassName, navGroup.className)} key={navGroup.id}>
                      {navGroup.items.map((item) => {
                        const isItemHidden = item.hidden && item.hidden({ user });

                        if (isItemHidden) {
                          return null;
                        }

                        // TODO: make without condition
                        if (item.id === 'support') {
                          return (
                            <button
                              onClick={() => {
                                closeDialog();
                                onSupport?.();
                              }}
                              key={item.id}
                              className={navItemClassName}
                            >
                              {item.label}
                            </button>
                          );
                        }

                        return (
                          <NavLayoutLink
                            onClick={closeDialog}
                            key={item.id}
                            // TODO: come up with better solution
                            href={item.href}
                            external={item.external}
                            className={navItemClassName}
                          >
                            {item.label}
                          </NavLayoutLink>
                        );
                      })}
                    </div>
                  );
                })}

                <div className={navGroupClassName}>
                  <button onClick={handleLogoutButtonClick} className={navItemClassName}>
                    Logout
                  </button>
                </div>
              </nav>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </ProfileMenuDialogPortal>
    </Dialog.Root>
  );
};
